import { ProductUploadType, TubeProduct, FileFormat } from './enum'
import { checkMultipleLine } from '@src/routes/Configurators/utils/util'

interface OligosInput {
  name: string
  fiveMod?: string
  fiveAndThreeMod?: string
  fiveAndThreeQuencher?: string
  nTermMode?: string
  cTermMode?: string
  amount?: string
  sequence: string
  threeMod?: string
  scale?: string
  purification?: string
  format?: string
  concentration?: string
  tubes?: string
  comments?: string | null
  purityLevel?: string
  conjugation?: string
  conjugationAmount?: string
  conjugationChemistry?: string
  additionalCarrierProtien?: string
  saltExchange?: string
  endotoxinTesting?: string
  filtration?: string
}

const buildOligosProduct = (oligo, header) => {
  const optionsSelect = 'Click for Options'
  const optionsSelectKeys = ['scale', 'fiveAndThreeQuencher', 'amount']
  const targetOligo = {}
  for (const key of header) {
    targetOligo[key] = ''
  }

  const returnOligo: OligosInput = Object.assign(targetOligo, oligo)
  for (const [key, value] of Object.entries(returnOligo)) {
    if (optionsSelectKeys.includes(key)) {
      returnOligo[key] =
        value && optionsSelect !== value ? value.toString().trim() : ''
    } else {
      returnOligo[key] = value ? value.toString().trim() : ''
    }
  }

  return returnOligo
}

const buildIscaleDNAProduct = (oligo) => {
  let {
    name,
    fiveMod,
    sequence,
    threeMod,
    amount,
    purification,
    tubes,
    saltExchange,
    filtration,
    endotoxinTesting,
    comments,
  } = oligo

  name = name ? name.toString().trim() : ''
  fiveMod = fiveMod ? fiveMod.toString().trim() : ''
  sequence = sequence ? sequence.toString().trim() : ''
  threeMod = threeMod ? threeMod.toString().trim() : ''
  amount = amount ? amount.toString().trim() : ''
  purification = purification ? purification.toString().trim() : ''
  tubes = tubes ? tubes.toString().trim() : ''
  comments = comments ? comments.toString().trim() : ''
  if (
    name === '' &&
    fiveMod === '' &&
    sequence === '' &&
    threeMod === '' &&
    amount === '' &&
    purification === '' &&
    tubes === '' &&
    comments === ''
  ) {
    saltExchange = saltExchange
      ? saltExchange.toString().trim() === 'Yes'
        ? ''
        : saltExchange.toString().trim()
      : ''
    endotoxinTesting = endotoxinTesting
      ? endotoxinTesting.toString().trim() === 'No'
        ? ''
        : endotoxinTesting.toString().trim()
      : ''
    filtration = filtration
      ? filtration.toString().trim() === 'No'
        ? ''
        : filtration.toString().trim()
      : ''
  } else {
    saltExchange = saltExchange ? saltExchange.toString().trim() : ''
    endotoxinTesting = endotoxinTesting
      ? endotoxinTesting.toString().trim()
      : ''
    filtration = filtration ? filtration.toString().trim() : ''
  }

  return {
    name,
    fiveMod,
    sequence,
    threeMod,
    amount,
    purification,
    tubes,
    saltExchange,
    filtration,
    endotoxinTesting,
    comments,
  }
}

export const parseSpreadsheet = async (
  file?: File,
  productType?: string
): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!file) {
      return resolve('')
    }

    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const XLSX = await import('xlsx')
        const data = new Uint8Array((e.target as any).result)
        const workbook = XLSX.read(data, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[sheetName]
        const hasHeaders = true
        const getProducts = (header) => {
          return (
            XLSX.utils.sheet_to_json(worksheet, {
              header,
              blankrows: true,
              defval: '',
            }) as OligosInput[]
          ).map(function (oligo) {
            return buildOligosProduct(oligo, header)
          })
        }
        let products: OligosInput[] = []
        let mergedProducts = ''
        if (productType === TubeProduct.Standard) {
          if (ProductUploadType.TubePCRUpload === sheetName) {
            worksheet['!ref'] = 'A1:E215'
            const header = ['name', 'sequence', 'scale', 'comments']
            products = getProducts(header)
            if (hasHeaders) {
              products.splice(0, 10)
              checkMultipleLine(products, resolve)
            }
          } else if (ProductUploadType.TubeDNAUpload === sheetName) {
            worksheet['!ref'] = 'A1:K215'
            const header = [
              'name',
              'fiveMod',
              'sequence',
              'threeMod',
              'scale',
              'purification',
              'format',
              'concentration',
              'tubes',
              'comments',
            ]
            products = getProducts(header)
            if (hasHeaders) {
              products.splice(0, 5)
              checkMultipleLine(products, resolve)
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
        } else if (productType === TubeProduct.Rna) {
          if (ProductUploadType.TubeRNAOligo === sheetName) {
            worksheet['!ref'] = 'A1:K215'
            const header = [
              'name',
              'fiveMod',
              'sequence',
              'threeMod',
              'scale',
              'purification',
              'tubes',
              'comments',
            ]
            products = getProducts(header)
            if (hasHeaders) {
              products.splice(0, 6)
              checkMultipleLine(products, resolve)
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
        } else if (productType === TubeProduct.Wellred) {
          if (ProductUploadType.Wellred === sheetName) {
            worksheet['!ref'] = 'A1:K215'
            const header = [
              'name',
              'fiveMod',
              'sequence',
              'amount',
              'purification',
              'tubes',
              'comments',
            ]
            products = getProducts(header)
            if (hasHeaders) {
              products.splice(0, 7)
              checkMultipleLine(products, resolve)
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
        } else if (productType === TubeProduct.Probe) {
          if (ProductUploadType.Probes === sheetName) {
            worksheet['!ref'] = 'A1:K215'
            const header = [
              'name',
              'fiveAndThreeMod',
              'sequence',
              'amount',
              'tubes',
              'comments',
            ]
            products = getProducts(header)
            if (hasHeaders) {
              products.splice(0, 7)
              checkMultipleLine(products, resolve)
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
        } else if (productType === TubeProduct.Aqua) {
          if (ProductUploadType.Aqua === sheetName) {
            worksheet['!ref'] = 'A1:K225'
            const header = [
              'name',
              'nTermMode',
              'sequence',
              'cTermMode',
              'comments',
            ]
            products = getProducts(header)
            if (hasHeaders) {
              products.splice(0, 20)
              checkMultipleLine(products, resolve)
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
        } else if (productType === TubeProduct.EasyOligo) {
          if (ProductUploadType.EasyOligos === sheetName) {
            worksheet['!ref'] = 'A1:K215'
            const header = ['name', 'sequence', 'comments']
            products = getProducts(header)
            if (hasHeaders) {
              products.splice(0, 9)
              checkMultipleLine(products, resolve)
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
        } else if (productType === TubeProduct.Beacon) {
          if (ProductUploadType.Beacons === sheetName) {
            worksheet['!ref'] = 'A1:K215'
            const header = [
              'name',
              'fiveAndThreeQuencher',
              'sequence',
              'amount',
              'tubes',
              'comments',
            ]
            products = getProducts(header)
            if (hasHeaders) {
              products.splice(0, 7)
              checkMultipleLine(products, resolve)
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
        } else if (productType === TubeProduct.LongOligo) {
          if (ProductUploadType.TubeDNAUpload === sheetName) {
            worksheet['!ref'] = 'A1:K215'
            const header = [
              'name',
              'fiveMod',
              'sequence',
              'threeMod',
              'purification',
              'comments',
            ]
            products = getProducts(header)
            if (hasHeaders) {
              products.splice(0, 9)
              checkMultipleLine(products, resolve)
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
        } else if (productType === TubeProduct.PureSimple) {
          if (ProductUploadType.PureSimple === sheetName) {
            worksheet['!ref'] = 'A1:K215'
            const header = ['name', 'sequence', 'comments']
            products = getProducts(header)
            if (hasHeaders) {
              products.splice(0, 8)
              checkMultipleLine(products, resolve)
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
        } else if (productType === TubeProduct.Cpeptide) {
          if (ProductUploadType.CustomPeptides === sheetName) {
            worksheet['!ref'] = 'A1:K215'
            const header = [
              'name',
              'nTermMode',
              'sequence',
              'cTermMode',
              'amount',
              'purityLevel',
              'conjugation',
              'conjugationAmount',
              'conjugationChemistry',
              'additionalCarrierProtien',
              'tubes',
              'comments',
            ]
            products = getProducts(header)
            if (hasHeaders) {
              const headersArray = products.splice(0, 4)
              if (headersArray[3]?.conjugationAmount === '')
                resolve(FileFormat.InvalidTemplate)
              else checkMultipleLine(products, resolve)
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
        } else if (productType === TubeProduct.iscaledna) {
          if (ProductUploadType.IscaleDna === sheetName) {
            worksheet['!ref'] = 'A1:K215'

            products = (
              XLSX.utils.sheet_to_json(worksheet, {
                header: [
                  'name',
                  'fiveMod',
                  'sequence',
                  'threeMod',
                  'amount',
                  'purification',
                  'tubes',
                  'saltExchange',
                  'filtration',
                  'endotoxinTesting',
                  'comments',
                ],
                blankrows: true,
                defval: '',
              }) as OligosInput[]
            ).map(buildIscaleDNAProduct)
            if (hasHeaders) {
              products.splice(0, 5)
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
        }
        products.forEach((product, index) => {
          let val = ''
          for (const value in product) {
            value !== 'comments'
              ? (val += `${product[value]}\t`)
              : (val += `${product[value]}`)
          }
          index !== products.length - 1
            ? (mergedProducts += `${val}\n`)
            : (mergedProducts += `${val}`)
        })

        resolve(mergedProducts)
      } catch (err) {
        reject(err)
      }
    }
    reader.readAsArrayBuffer(file)
  })
